import { z } from 'zod';
import { ChannelKindSchema, InviteStatusSchema } from './enums';
import { PageQuerySchema } from './common';

/**
 * §7.1, §7.3, §18 ТЗ. Приглашение администратора заказчика.
 *
 * Открытый токен и PIN не хранятся: в БД лежат только HMAC и argon2id.
 * Поэтому в списке приглашений секретов нет и быть не может — они существуют
 * ровно один раз, в ответе на создание.
 */

export const CreateInviteRequestSchema = z.object({
  projectId: z.string().uuid(),
  /** Пусто — берётся настройка `invite_ttl_hours` (§26, дефолт 72). */
  ttlHours: z.number().int().min(1).max(720).optional(),
});
export type CreateInviteRequest = z.infer<typeof CreateInviteRequestSchema>;

/** Ссылка на бота с одноразовым payload. Идентификатора объекта в ней нет (§7.1). */
export const InviteLinkSchema = z.object({
  channel: ChannelKindSchema,
  url: z.string().url(),
});
export type InviteLink = z.infer<typeof InviteLinkSchema>;

/**
 * Секреты приглашения. Отдаются **однократно**, в ответе на создание.
 * Повторно получить их нельзя — только выпустить новое приглашение.
 */
export const InviteSecretsSchema = z.object({
  token: z.string(),
  pin: z.string().regex(/^\d{6}$/),
  links: z.array(InviteLinkSchema),
});
export type InviteSecrets = z.infer<typeof InviteSecretsSchema>;

export const InviteSummarySchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  projectName: z.string(),
  status: InviteStatusSchema,
  expiresAt: z.string().datetime(),
  attempts: z.number().int().min(0),
  maxAttempts: z.number().int().min(1),
  createdAt: z.string().datetime(),
  createdByName: z.string(),
  usedAt: z.string().datetime().nullable(),
  usedByPersonId: z.string().uuid().nullable(),
  usedByName: z.string().nullable(),
  revokedAt: z.string().datetime().nullable(),
});
export type InviteSummary = z.infer<typeof InviteSummarySchema>;

export const CreateInviteResponseSchema = z.object({
  invite: InviteSummarySchema,
  secrets: InviteSecretsSchema,
});
export type CreateInviteResponse = z.infer<typeof CreateInviteResponseSchema>;

export const ListInvitesQuerySchema = PageQuerySchema.extend({
  projectId: z.string().uuid().optional(),
  status: InviteStatusSchema.optional(),
});
export type ListInvitesQuery = z.infer<typeof ListInvitesQuerySchema>;
